(function (root, factory) {
  if (typeof module === 'object' && module.exports) {
    module.exports = factory(root);
  } else {
    root.HLA_BUILD_A_LOOK = factory(root);
    root.HLA_BUILD_A_LOOK.initAll();
  }
})(typeof window !== 'undefined' ? window : globalThis, function (root) {
  'use strict';

  const API_BASE = 'https://erp.haydenla.com/api/outfit-pairings';
  const DEFAULT_SEED_HANDLE = 'ribbed-puff-sleeve-top';
  const DEFAULT_LIMIT = 24;
  const MAX_LIMIT = 40;
  const DEFAULT_TIMEOUT_MS = 6000;
  const ALL_OCCASIONS = 'all';
  const SLOT_CONFIG = [
    { id: 'tops', label: 'Tops', empty: 'No top pairings found.' },
    { id: 'bottoms', label: 'Bottoms', empty: 'No bottom pairings found.' },
    { id: 'dresses', label: 'Dresses', empty: 'No dress pairings found.' },
    { id: 'outerwear', label: 'Outerwear', empty: 'No outerwear pairings found.' },
    { id: 'set', label: 'Set', empty: 'No matching set pairings found.' },
  ];
  const SLOT_ALIASES = {
    top: 'tops',
    tops: 'tops',
    blouse: 'tops',
    blouses: 'tops',
    bottom: 'bottoms',
    bottoms: 'bottoms',
    pant: 'bottoms',
    pants: 'bottoms',
    skirt: 'bottoms',
    skirts: 'bottoms',
    short: 'bottoms',
    shorts: 'bottoms',
    dress: 'dresses',
    dresses: 'dresses',
    outerwear: 'outerwear',
    jacket: 'outerwear',
    jackets: 'outerwear',
    cardigan: 'outerwear',
    cardigans: 'outerwear',
    set: 'set',
    sets: 'set',
    counterpart: 'set',
  };
  const OCCASION_LABELS = {
    'date-night': 'Date Night',
    brunch: 'Brunch',
    'work-office': 'Work Office',
    office: 'Work Office',
    work: 'Work Office',
    'girls-night': 'Girls Night',
    'wedding-guest': 'Wedding Guest',
    athleisure: 'Athleisure',
    vacation: 'Vacation',
    resort: 'Resort',
    travel: 'Travel',
    casual: 'Casual',
    weekend: 'Weekend',
    party: 'Party',
    holiday: 'Holiday',
    church: 'Church',
  };
  const OCCASION_ORDER = [
    'date-night',
    'brunch',
    'work-office',
    'girls-night',
    'wedding-guest',
    'athleisure',
    'vacation',
    'resort',
    'travel',
    'casual',
    'weekend',
    'party',
    'holiday',
    'church',
  ];

  function normalizeString(value) {
    return String(value || '').replace(/\s+/g, ' ').trim();
  }

  function flattenValues(value) {
    if (value == null) return [];
    if (Array.isArray(value)) return value.flatMap(flattenValues);
    if (typeof value === 'object') {
      if (Object.prototype.hasOwnProperty.call(value, 'value')) return flattenValues(value.value);
      return Object.values(value).flatMap(flattenValues);
    }
    return [normalizeString(value)].filter(Boolean);
  }

  function deriveLineFromProduct(product) {
    const lineValues = flattenValues([
      product && product.line,
      product && product.custom_line,
      product && product.customLine,
      product && product.metafields && product.metafields.custom && product.metafields.custom.line,
    ]).map(value => value.toLowerCase());
    const tagValues = flattenValues(product && product.tags).map(value => value.toLowerCase());
    const typeValues = flattenValues([
      product && product.product_type,
      product && product.type,
    ]).map(value => value.toLowerCase());
    const fallbackValues = flattenValues([
      product && product.vendor,
      product && product.title,
      product && product.handle,
    ]).map(value => value.toLowerCase());

    const hasLine = (values, pattern) => values.some(value => pattern.test(value));
    if (hasLine(lineValues, /^(plus|hayden plus)$/)) return 'Plus';
    if (hasLine(lineValues, /^(womens?|women'?s|hayden la)$/)) return 'Womens';
    if (hasLine(lineValues, /^(girls?|hayden girls?)$/)) return 'Girls';

    if (hasLine(tagValues, /^(plus|hayden plus)$/)) return 'Plus';
    if (hasLine(tagValues, /^(womens?|women'?s|hayden la)$/)) return 'Womens';
    if (hasLine(tagValues, /^(girls?|hayden girls?)$/)) return 'Girls';

    const typeText = typeValues.join(' | ');
    if (/\b(plus|curve|extended size)\b/.test(typeText)) return 'Plus';
    if (/\b(womens?|women'?s|missy)\b/.test(typeText)) return 'Womens';
    if (/\bgirls?\b/.test(typeText)) return 'Girls';

    const fallbackText = fallbackValues.join(' | ');
    if (/\b(hayden plus|plus)\b/.test(fallbackText)) return 'Plus';
    if (/\b(hayden girls?|girls?)\b/.test(fallbackText)) return 'Girls';
    if (/\b(womens?|women'?s|hayden los angeles|hayden la|missy)\b/.test(fallbackText)) return 'Womens';
    return '';
  }

  function normalizeHandle(value) {
    return normalizeString(value).toLowerCase();
  }

  function clampLimit(value) {
    const parsed = Number(value);
    if (!Number.isFinite(parsed) || parsed <= 0) return DEFAULT_LIMIT;
    return Math.min(MAX_LIMIT, Math.max(1, Math.floor(parsed)));
  }

  function normalizeSlot(value) {
    return SLOT_ALIASES[normalizeString(value).toLowerCase().replace(/[_\s-]+/g, '-')]
      || SLOT_ALIASES[normalizeString(value).toLowerCase()]
      || '';
  }

  function slotLabel(slot) {
    const config = SLOT_CONFIG.find(item => item.id === slot);
    return config ? config.label : normalizeString(slot);
  }

  function normalizeOccasionTag(value) {
    return normalizeString(value)
      .toLowerCase()
      .replace(/&/g, 'and')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  function occasionLabel(slug, fallback) {
    if (OCCASION_LABELS[slug]) return OCCASION_LABELS[slug];
    const value = normalizeString(fallback || slug).replace(/[_-]+/g, ' ');
    return value.replace(/\b[a-z]/g, letter => letter.toUpperCase());
  }

  function normalizeOccasionTags(value) {
    if (!Array.isArray(value)) return { raw: [], slugs: [] };
    const raw = [];
    const slugs = [];
    value.forEach(tag => {
      const label = normalizeString(tag);
      const slug = normalizeOccasionTag(label);
      if (!slug || slugs.includes(slug)) return;
      raw.push(label);
      slugs.push(slug);
    });
    return { raw, slugs };
  }

  function buildPairApiUrl(handle, limit = DEFAULT_LIMIT, occasion = ALL_OCCASIONS) {
    const url = new URL(API_BASE);
    url.searchParams.set('store', 'ws');
    url.searchParams.set('handle', normalizeHandle(handle));
    url.searchParams.set('limit', String(clampLimit(limit)));
    const occasionSlug = normalizeOccasionTag(occasion);
    if (occasionSlug && occasionSlug !== ALL_OCCASIONS) {
      url.searchParams.set('occasion', occasionSlug);
    }
    return url.toString();
  }

  function helperApi() {
    return root.HLA_STYLES_THAT_PAIR || {};
  }

  function buildProductJsonUrl(handle) {
    const helpers = helperApi();
    if (typeof helpers.buildProductJsonUrl === 'function') {
      return helpers.buildProductJsonUrl(handle);
    }
    return `/products/${encodeURIComponent(handle)}.js`;
  }

  function fetchWithTimeout(url, options = {}, timeoutMs = DEFAULT_TIMEOUT_MS) {
    const Controller = root.AbortController || (typeof AbortController !== 'undefined' ? AbortController : null);
    const controller = Controller ? new Controller() : null;
    const requestOptions = { ...options };
    let timer = null;
    if (controller) {
      requestOptions.signal = controller.signal;
      timer = setTimeout(() => controller.abort(), timeoutMs);
    }
    return root.fetch(url, requestOptions).finally(() => {
      if (timer) clearTimeout(timer);
    });
  }

  async function fetchJson(url, timeoutMs, label) {
    const response = await fetchWithTimeout(url, {
      credentials: String(url).startsWith('/products/') ? 'same-origin' : 'omit',
      headers: { Accept: 'application/json' },
    }, timeoutMs);
    if (!response.ok) throw new Error(`${label}-${response.status}`);
    return response.json();
  }

  function normalizePairResponse(data, options = {}) {
    if (!data || data.ok !== true || !Array.isArray(data.pairs)) return [];
    const limit = clampLimit(options.limit);
    const sourceHandle = normalizeHandle(options.sourceHandle || (data.source && data.source.handle));
    const seen = new Set();
    const pairs = [];

    data.pairs.forEach((pair, index) => {
      if (pairs.length >= limit || !pair || typeof pair !== 'object') return;
      const handle = normalizeHandle(pair.handle);
      const slot = normalizeSlot(pair.slot);
      if (!handle || handle === sourceHandle || !slot || seen.has(`${slot}:${handle}`)) return;
      seen.add(`${slot}:${handle}`);
      const occasions = normalizeOccasionTags(pair.occasion_tags);
      const reasons = Array.isArray(pair.reasons)
        ? pair.reasons.map(normalizeString).filter(Boolean).slice(0, 2)
        : [];

      pairs.push({
        index,
        handle,
        title: normalizeString(pair.title),
        url: normalizeString(pair.url) || `/products/${handle}`,
        slot,
        slotLabel: slotLabel(slot),
        score: Number.isFinite(Number(pair.score)) ? Number(pair.score) : null,
        reasons,
        occasionTags: occasions.raw,
        occasionSlugs: occasions.slugs,
        isCounterpart: pair.is_counterpart === true,
      });
    });

    return pairs;
  }

  function makeNode(doc, tagName, className, text) {
    const node = doc.createElement(tagName);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function clearNode(node) {
    if (!node) return;
    if (typeof node.replaceChildren === 'function') {
      node.replaceChildren();
    } else {
      while (node.firstChild) node.removeChild(node.firstChild);
    }
  }

  function setHidden(node, hidden) {
    if (node) node.hidden = hidden;
  }

  function setClass(node, className, enabled) {
    if (!node) return;
    if (node.classList && typeof node.classList.toggle === 'function') {
      node.classList.toggle(className, enabled);
      return;
    }
    const classes = new Set(normalizeString(node.className).split(/\s+/).filter(Boolean));
    if (enabled) classes.add(className);
    else classes.delete(className);
    node.className = Array.from(classes).join(' ');
  }

  function money(cents) {
    const value = Number(cents || 0);
    return `$${(Number.isFinite(value) ? value / 100 : 0).toFixed(2)}`;
  }

  function centsFromMoney(value) {
    const match = normalizeString(value).match(/-?\$?([0-9,]+(?:\.[0-9]{1,2})?)/);
    if (!match) return 0;
    return Math.round(Number(match[1].replace(/,/g, '')) * 100);
  }

  function itemFromCard(card) {
    if (!card) return null;
    const unitCents = Number(card.dataset.unitCents || 0) || centsFromMoney(card.dataset.unitPrice);
    const packCents = Number(card.dataset.packCents || 0) || centsFromMoney(card.dataset.packPrice);
    const handle = normalizeHandle(card.dataset.productHandle || card.dataset.pairHandle);
    const variantId = normalizeString(card.dataset.variantId || card.dataset.pairCardVariantId);
    return {
      key: `product:${card.dataset.productId || handle}:${variantId || card.dataset.sku || ''}`,
      productId: normalizeString(card.dataset.productId),
      variantId,
      handle,
      title: normalizeString(card.dataset.productTitle) || handle,
      url: normalizeString(card.dataset.productUrl) || (handle ? `/products/${handle}` : '#'),
      sku: normalizeString(card.dataset.sku),
      image: normalizeString(card.dataset.cardImage),
      unitCents,
      packCents,
      unitPrice: normalizeString(card.dataset.unitPrice) || money(unitCents),
      packPrice: normalizeString(card.dataset.packPrice) || money(packCents),
      packSize: normalizeString(card.dataset.packSize),
      sizeRatio: normalizeString(card.dataset.sizeRatio),
      availability: normalizeString(card.dataset.availability),
      available: card.dataset.pairCardAvailable !== 'false',
      slot: normalizeSlot(card.dataset.lookSlot || card.dataset.pairSlot),
      line: normalizeString(card.dataset.line),
      isCounterpart: card.dataset.lookCounterpart === 'true',
      occasionSlugs: normalizeString(card.dataset.lookOccasions).split(',').filter(Boolean),
    };
  }

  function itemFromProduct(product, pair) {
    const helpers = helperApi();
    const data = typeof helpers.productCardData === 'function'
      ? helpers.productCardData(product, pair)
      : null;
    if (!data) return placeholderItem(pair.handle, pair.slot, pair.title);
    return {
      key: `product:${data.productId || data.handle}:${data.variantId || data.sku || ''}`,
      productId: normalizeString(data.productId),
      variantId: normalizeString(data.variantId),
      handle: normalizeHandle(data.handle || pair.handle),
      title: normalizeString(data.title || pair.title),
      url: normalizeString(data.url) || (data.handle ? `/products/${data.handle}` : '#'),
      sku: normalizeString(data.sku),
      image: normalizeString(data.image),
      unitCents: Number(data.unitCents || 0) || centsFromMoney(data.unitPrice),
      packCents: Number(data.packCents || 0) || centsFromMoney(data.packPrice),
      unitPrice: normalizeString(data.unitPrice),
      packPrice: normalizeString(data.packPrice),
      packSize: normalizeString(data.packSize),
      sizeRatio: normalizeString(data.sizeRatio),
      availability: normalizeString(data.availability),
      available: data.available !== false,
      slot: pair.slot,
      line: normalizeString(data.line || pair.line),
      isCounterpart: pair.isCounterpart === true,
      occasionSlugs: Array.isArray(pair.occasionSlugs) ? pair.occasionSlugs.slice() : [],
    };
  }

  function placeholderItem(handle, slot = 'seed', title = '') {
    const normalizedHandle = normalizeHandle(handle);
    return {
      key: `product:${normalizedHandle}:missing`,
      productId: '',
      variantId: '',
      handle: normalizedHandle,
      title: normalizeString(title) || normalizedHandle.replace(/-/g, ' '),
      url: normalizedHandle ? `/products/${normalizedHandle}` : '#',
      sku: '',
      image: '',
      unitCents: 0,
      packCents: 0,
      unitPrice: '$0.00',
      packPrice: '$0.00',
      packSize: '',
      sizeRatio: '',
      availability: 'Unavailable',
      available: false,
      slot,
      line: '',
      isCounterpart: false,
      occasionSlugs: [],
    };
  }

  function cartItemsFromItems(items) {
    return items
      .filter(item => item && item.variantId && item.available !== false)
      .filter(item => normalizeString(item.availability).toLowerCase() !== 'sold out')
      .map(item => ({ id: Number(item.variantId), quantity: 1 }))
      .filter(item => Number.isFinite(item.id) && item.id > 0);
  }

  async function addItemsToCart(items, button) {
    const helpers = helperApi();
    if (typeof helpers.addItemsToCart === 'function') {
      await helpers.addItemsToCart(items, button);
      return;
    }
    if (!items.length) return;
    const response = await root.fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ items }),
    });
    if (!response.ok) throw new Error(`cart-add-${response.status}`);
  }

  function selectedItems(seedItem, selected) {
    return [seedItem, ...Object.values(selected)].filter(Boolean);
  }

  function deriveOccasionOptions(pairs) {
    const options = new Map();
    pairs.forEach(pair => {
      pair.occasionSlugs.forEach((slug, index) => {
        if (!options.has(slug)) {
          options.set(slug, occasionLabel(slug, pair.occasionTags[index]));
        }
      });
    });
    return Array.from(options.entries())
      .map(([slug, label]) => ({ slug, label }))
      .sort((left, right) => {
        const leftIndex = OCCASION_ORDER.indexOf(left.slug);
        const rightIndex = OCCASION_ORDER.indexOf(right.slug);
        const leftRank = leftIndex === -1 ? 999 : leftIndex;
        const rightRank = rightIndex === -1 ? 999 : rightIndex;
        if (leftRank !== rightRank) return leftRank - rightRank;
        return left.label.localeCompare(right.label);
      });
  }

  function isInteractiveTarget(target) {
    return !!target.closest('a, button, input, select, textarea, [data-hla-quick-view], [data-hla-favorite-toggle]');
  }

  function initBuilder(rootNode) {
    if (!rootNode || rootNode.dataset.hlaBuildLookReady === 'true') {
      return rootNode && rootNode._hlaBuildLookController;
    }
    rootNode.dataset.hlaBuildLookReady = 'true';

    const doc = rootNode.ownerDocument || root.document;
    const limit = clampLimit(rootNode.dataset.limit);
    const timeoutMs = Number(rootNode.dataset.timeoutMs) || DEFAULT_TIMEOUT_MS;
    const seedInput = rootNode.querySelector('[data-hla-look-seed-input]');
    const seedForm = rootNode.querySelector('[data-hla-look-seed-form]');
    const status = rootNode.querySelector('[data-hla-look-status]');
    const seedCard = rootNode.querySelector('[data-hla-look-seed-card]');
    const summaryItems = rootNode.querySelector('[data-hla-look-summary-items]');
    const unitSubtotal = rootNode.querySelector('[data-hla-look-unit-subtotal]');
    const packSubtotal = rootNode.querySelector('[data-hla-look-pack-subtotal]');
    const saveButton = rootNode.querySelector('[data-hla-look-save]');
    const addButton = rootNode.querySelector('[data-hla-look-add]');
    const chips = rootNode.querySelector('[data-hla-look-occasion-chips]');
    const eyebrow = rootNode.querySelector('[data-hla-look-eyebrow]') || rootNode.querySelector('.hla-build-look__eyebrow');
    const emptyState = rootNode.querySelector('[data-hla-look-empty-state]');
    const productCache = new Map();
    let seedHandle = '';
    let seedItem = null;
    let selected = {};
    let allPairs = [];
    let activeOccasion = ALL_OCCASIONS;
    let renderToken = 0;
    let loadToken = 0;

    function setStatus(message, state = '') {
      if (status) status.textContent = message || '';
      if (state) rootNode.dataset.hlaBuildLookState = state;
    }

    function updateEyebrow(line) {
      const normalizedLine = normalizeString(line);
      rootNode.dataset.line = normalizedLine;
      if (!eyebrow) return;
      eyebrow.textContent = normalizedLine ? `${normalizedLine.toUpperCase()} OUTFIT RECOMMENDER` : 'OUTFIT RECOMMENDER';
    }

    function updateUrlSeed(handle) {
      if (!root.history || !root.location || !root.URL) return;
      try {
        const url = new root.URL(root.location.href);
        url.searchParams.set('seed', handle);
        root.history.replaceState({}, '', url.toString());
      } catch (_) {}
    }

    function seedFromLocation() {
      if (!root.location || !root.URLSearchParams) return '';
      try {
        return normalizeHandle(new root.URLSearchParams(root.location.search).get('seed'));
      } catch (_) {
        return '';
      }
    }

    async function fetchProduct(handle) {
      const normalizedHandle = normalizeHandle(handle);
      if (productCache.has(normalizedHandle)) return productCache.get(normalizedHandle);
      const product = await fetchJson(buildProductJsonUrl(normalizedHandle), timeoutMs, 'product-json');
      productCache.set(normalizedHandle, product);
      return product;
    }

    async function fetchPairs(handle, occasion = ALL_OCCASIONS) {
      const data = await fetchJson(buildPairApiUrl(handle, limit, occasion), timeoutMs, 'pairing-api');
      return normalizePairResponse(data, { sourceHandle: handle, limit });
    }

    function setAllEmptyState(visible) {
      if (emptyState) emptyState.hidden = !visible;
    }

    function renderSlotShell(slot) {
      const card = rootNode.querySelector(`[data-hla-look-slot-card="${slot}"]`);
      if (!card) return;
      card.dataset.hlaLookSlotState = 'empty';
      clearNode(card);
      const empty = makeNode(doc, 'div', 'hla-look-slot__empty');
      empty.appendChild(makeNode(doc, 'span', '', slotLabel(slot)));
      card.appendChild(empty);
    }

    function renderItemCard(card, item, label, options = {}) {
      if (!card) return;
      clearNode(card);
      card.dataset.hlaLookSlotState = item ? 'filled' : 'empty';
      if (!item) {
        const empty = makeNode(doc, 'div', 'hla-look-slot__empty');
        empty.appendChild(makeNode(doc, 'span', '', label));
        card.appendChild(empty);
        return;
      }

      card.dataset.productHandle = item.handle;
      card.dataset.variantId = item.variantId;
      card.dataset.unitCents = String(item.unitCents || 0);
      card.dataset.packCents = String(item.packCents || 0);
      card.dataset.line = item.line || rootNode.dataset.line || '';

      const imageWrap = makeNode(doc, 'div', 'hla-look-slot__image');
      if (item.image) {
        const link = makeNode(doc, 'a');
        link.href = item.url;
        const image = makeNode(doc, 'img');
        image.src = item.image;
        image.alt = item.title;
        image.loading = 'lazy';
        link.appendChild(image);
        imageWrap.appendChild(link);
      }

      const body = makeNode(doc, 'div', 'hla-look-slot__body');
      body.appendChild(makeNode(doc, 'p', '', label));
      const heading = makeNode(doc, 'h3');
      const titleLink = makeNode(doc, 'a', '', item.title);
      titleLink.href = item.url;
      heading.appendChild(titleLink);
      body.appendChild(heading);

      const meta = makeNode(doc, 'dl');
      [
        ['Unit', item.unitPrice || money(item.unitCents)],
        ['Pack', item.packSize || item.sizeRatio ? [item.packSize ? `${item.packSize} pcs` : '', item.sizeRatio].filter(Boolean).join(' ') : item.packPrice || money(item.packCents)],
      ].forEach(([term, value]) => {
        const row = makeNode(doc, 'div');
        row.appendChild(makeNode(doc, 'dt', '', term));
        row.appendChild(makeNode(doc, 'dd', '', value || 'Not available'));
        meta.appendChild(row);
      });
      body.appendChild(meta);

      if (item.isCounterpart) {
        const badge = makeNode(doc, 'span', 'hla-look-slot__badge', 'Matching set');
        body.appendChild(badge);
      }

      const actions = makeNode(doc, 'div', 'hla-look-slot__actions');
      const quick = makeNode(doc, 'button', 'link', 'Quick View');
      quick.type = 'button';
      quick.dataset.hlaLookQuick = options.slot || item.slot;
      actions.appendChild(quick);

      const savePiece = makeNode(doc, 'button', 'link', 'Save Piece');
      savePiece.type = 'button';
      savePiece.dataset.hlaLookSavePiece = options.slot || item.slot;
      actions.appendChild(savePiece);

      if (!options.seed) {
        const swap = makeNode(doc, 'button', 'link', 'Swap');
        swap.type = 'button';
        swap.dataset.hlaLookSwap = options.slot || item.slot;
        actions.appendChild(swap);

        const clear = makeNode(doc, 'button', 'link', 'Clear');
        clear.type = 'button';
        clear.dataset.hlaLookClear = options.slot || item.slot;
        actions.appendChild(clear);
      }
      body.appendChild(actions);

      card.appendChild(imageWrap);
      card.appendChild(body);
    }

    function renderCanvas() {
      renderItemCard(seedCard, seedItem, 'Starting piece', { seed: true, slot: 'seed' });
      SLOT_CONFIG.forEach(slot => {
        const card = rootNode.querySelector(`[data-hla-look-slot-card="${slot.id}"]`);
        renderItemCard(card, selected[slot.id], slot.label, { slot: slot.id });
      });
    }

    function renderSummary() {
      const items = selectedItems(seedItem, selected);
      clearNode(summaryItems);
      if (summaryItems) {
        if (!items.length) {
          summaryItems.appendChild(makeNode(doc, 'p', 'hla-look-summary__empty', 'No pieces selected.'));
        } else {
          items.forEach(item => {
            const row = makeNode(doc, 'div', 'hla-look-summary__item');
            row.appendChild(makeNode(doc, 'span', '', item.title));
            row.appendChild(makeNode(doc, 'strong', '', item.unitPrice || money(item.unitCents)));
            summaryItems.appendChild(row);
          });
        }
      }

      const unitTotal = items.reduce((sum, item) => sum + Number(item.unitCents || 0), 0);
      const packTotal = items.reduce((sum, item) => sum + Number(item.packCents || 0), 0);
      if (unitSubtotal) unitSubtotal.textContent = money(unitTotal);
      if (packSubtotal) packSubtotal.textContent = money(packTotal);
      const addable = cartItemsFromItems(items);
      if (saveButton) saveButton.disabled = items.length === 0;
      if (addButton) addButton.disabled = addable.length === 0;
    }

    function refreshCardActions() {
      const actions = root.HLA_CARD_ACTIONS;
      if (actions && typeof actions.refresh === 'function') {
        actions.refresh();
      } else if (actions && typeof actions.updateFavoriteButtons === 'function') {
        actions.updateFavoriteButtons();
      }
    }

    async function buildRailCard(pair) {
      const helpers = helperApi();
      if (typeof helpers.buildProductCard !== 'function') return null;
      const product = await fetchProduct(pair.handle);
      const card = helpers.buildProductCard(product, pair, doc);
      if (!card) return null;
      const data = typeof helpers.productCardData === 'function' ? helpers.productCardData(product, pair) : null;
      card.dataset.hlaLookRailCard = '';
      card.dataset.lookSlot = pair.slot;
      card.dataset.lookCounterpart = pair.isCounterpart ? 'true' : 'false';
      card.dataset.lookOccasions = pair.occasionSlugs.join(',');
      if (data) {
        card.dataset.unitCents = String(data.unitCents || 0);
        card.dataset.packCents = String(data.packCents || 0);
      }
      setClass(card, 'is-selected', selected[pair.slot] && selected[pair.slot].handle === pair.handle);
      return card;
    }

    function updateRailState(slot, count) {
      const rail = rootNode.querySelector(`[data-hla-look-rail="${slot.id}"]`);
      const empty = rootNode.querySelector(`[data-hla-look-rail-empty="${slot.id}"]`);
      const countNode = rootNode.querySelector(`[data-hla-look-rail-count="${slot.id}"]`);
      setHidden(rail, count === 0);
      if (empty) {
        empty.textContent = '';
        empty.hidden = true;
      }
      if (countNode) countNode.textContent = count ? `${count}` : '';
    }

    async function renderRails(pairs) {
      const token = ++renderToken;
      let total = 0;
      setAllEmptyState(false);
      await Promise.all(SLOT_CONFIG.map(async slot => {
        const grid = rootNode.querySelector(`[data-hla-look-rail-grid="${slot.id}"]`);
        if (!grid) {
          updateRailState(slot, 0);
          return;
        }
        clearNode(grid);
        updateRailState(slot, 0);
        const slotPairs = pairs.filter(pair => pair.slot === slot.id);
        const settled = await Promise.allSettled(slotPairs.map(buildRailCard));
        if (token !== renderToken) return;
        const cards = settled
          .filter(result => result.status === 'fulfilled' && result.value)
          .map(result => result.value);
        total += cards.length;
        cards.forEach(card => grid.appendChild(card));
        updateRailState(slot, cards.length);
      }));
      if (token !== renderToken) return;
      setAllEmptyState(total === 0);
      refreshCardActions();
      renderSummary();
      setStatus(total ? `Showing ${total} recommendations.` : 'No recommendations found for this starting piece.', total ? 'ready' : 'empty');
    }

    function renderOccasionChips() {
      if (!chips) return;
      clearNode(chips);
      const all = makeNode(doc, 'button', 'hla-look-chip', 'View All');
      all.type = 'button';
      all.dataset.hlaLookOccasion = ALL_OCCASIONS;
      setClass(all, 'is-active', activeOccasion === ALL_OCCASIONS);
      chips.appendChild(all);

      deriveOccasionOptions(allPairs).forEach(option => {
        const button = makeNode(doc, 'button', 'hla-look-chip', option.label);
        button.type = 'button';
        button.dataset.hlaLookOccasion = option.slug;
        setClass(button, 'is-active', activeOccasion === option.slug);
        chips.appendChild(button);
      });
    }

    async function selectOccasion(occasion) {
      activeOccasion = normalizeOccasionTag(occasion) || ALL_OCCASIONS;
      renderOccasionChips();
      if (activeOccasion === ALL_OCCASIONS) {
        await renderRails(allPairs);
        return;
      }

      setStatus(`Loading ${occasionLabel(activeOccasion)} recommendations.`, 'loading');
      let pairs = [];
      try {
        pairs = await fetchPairs(seedHandle, activeOccasion);
      } catch (_) {
        pairs = [];
      }
      if (!pairs.length) {
        pairs = allPairs.filter(pair => pair.occasionSlugs.includes(activeOccasion));
      }
      await renderRails(pairs);
    }

    function placeCard(card) {
      const item = itemFromCard(card);
      if (!item || !item.slot) return;
      selected[item.slot] = item;
      renderCanvas();
      renderSummary();
      rootNode.querySelectorAll('[data-hla-look-rail-card]').forEach(railCard => {
        setClass(railCard, 'is-selected', selected[railCard.dataset.lookSlot] && selected[railCard.dataset.lookSlot].handle === railCard.dataset.productHandle);
      });
    }

    function clearSlot(slot) {
      const normalizedSlot = normalizeSlot(slot);
      if (!normalizedSlot) return;
      delete selected[normalizedSlot];
      renderCanvas();
      renderSummary();
      rootNode.querySelectorAll('[data-hla-look-rail-card]').forEach(railCard => {
        setClass(railCard, 'is-selected', selected[railCard.dataset.lookSlot] && selected[railCard.dataset.lookSlot].handle === railCard.dataset.productHandle);
      });
    }

    function swapSlot(slot) {
      const normalizedSlot = normalizeSlot(slot);
      if (!normalizedSlot) return;
      const cards = Array.from(rootNode.querySelectorAll(`[data-hla-look-rail-grid="${normalizedSlot}"] [data-hla-look-rail-card]`));
      if (!cards.length) return;
      const currentHandle = selected[normalizedSlot] && selected[normalizedSlot].handle;
      const currentIndex = cards.findIndex(card => normalizeHandle(card.dataset.productHandle) === currentHandle);
      const nextIndex = currentIndex >= 0 ? (currentIndex + 1) % cards.length : 0;
      placeCard(cards[nextIndex]);
    }

    function favoriteApi() {
      return root.HLA_CARD_ACTIONS || null;
    }

    function savePiece(slot) {
      const item = slot === 'seed' ? seedItem : selected[normalizeSlot(slot)];
      const api = favoriteApi();
      if (!item || !api || typeof api.saveItem !== 'function') return;
      api.saveItem({ ...item, source: 'build_a_look' }, 'save');
    }

    function saveLook() {
      const items = selectedItems(seedItem, selected);
      const api = favoriteApi();
      if (!items.length || !api || typeof api.saveItem !== 'function') return;
      const unitTotal = items.reduce((sum, item) => sum + Number(item.unitCents || 0), 0);
      const packTotal = items.reduce((sum, item) => sum + Number(item.packCents || 0), 0);
      const key = `look:${rootNode.dataset.line || 'line'}:${items.map(item => item.variantId || item.sku || item.handle).join('|')}`;
      const first = items[0] || {};
      api.saveItem({
        key,
        type: 'look',
        source: 'build_a_look',
        title: `${rootNode.dataset.heading || 'Build a Look'} (${items.length})`,
        image: first.image || '',
        url: first.url || '#',
        look: {
          id: key,
          line: rootNode.dataset.line || '',
          seed: seedHandle,
          occasion: activeOccasion,
          itemCount: items.length,
          subtotal: money(unitTotal),
          packSubtotal: money(packTotal),
          pieces: items.map(item => ({
            slot: item.slot,
            productId: item.productId,
            variantId: item.variantId,
            handle: item.handle,
            sku: item.sku,
            title: item.title,
            unitPrice: item.unitPrice || money(item.unitCents),
            packPrice: item.packPrice || money(item.packCents),
          })),
        },
      }, 'save_look');
    }

    async function addLook(button = addButton) {
      const items = cartItemsFromItems(selectedItems(seedItem, selected));
      if (!items.length) return [];
      await addItemsToCart(items, button);
      return items;
    }

    async function loadSeed(nextSeed) {
      const normalizedSeed = normalizeHandle(nextSeed) || DEFAULT_SEED_HANDLE;
      const token = ++loadToken;
      setStatus('Loading recommendations.', 'loading');

      let product = null;
      try {
        product = await fetchProduct(normalizedSeed);
      } catch (_) {
        if (token !== loadToken) return;
        if (seedItem) {
          if (seedInput) seedInput.value = seedHandle;
          updateUrlSeed(seedHandle);
          setStatus('Starting piece not found. Keeping current look.', 'ready');
          return;
        }
        seedHandle = normalizedSeed;
        seedItem = placeholderItem(normalizedSeed, 'seed');
        selected = {};
        allPairs = [];
        activeOccasion = ALL_OCCASIONS;
        if (seedInput) seedInput.value = normalizedSeed;
        updateUrlSeed(normalizedSeed);
        updateEyebrow('');
        SLOT_CONFIG.forEach(slot => renderSlotShell(slot.id));
        renderOccasionChips();
        renderCanvas();
        renderSummary();
        await renderRails([]);
        setStatus('Starting piece not found.', 'empty');
        return;
      }
      if (token !== loadToken) return;

      const seedLine = deriveLineFromProduct(product);
      seedHandle = normalizedSeed;
      seedItem = itemFromProduct(product, {
        handle: normalizedSeed,
        title: product && product.title,
        url: product && product.url,
        slot: 'seed',
        slotLabel: 'Starting piece',
        line: seedLine,
      });
      if (seedItem) seedItem.line = seedLine;
      selected = {};
      allPairs = [];
      activeOccasion = ALL_OCCASIONS;
      if (seedInput) seedInput.value = normalizedSeed;
      updateUrlSeed(normalizedSeed);
      updateEyebrow(seedLine);
      SLOT_CONFIG.forEach(slot => renderSlotShell(slot.id));
      renderOccasionChips();
      renderCanvas();
      renderSummary();

      try {
        allPairs = await fetchPairs(normalizedSeed, ALL_OCCASIONS);
        if (token !== loadToken) return;
        renderOccasionChips();
        await renderRails(allPairs);
      } catch (_) {
        if (token !== loadToken) return;
        allPairs = [];
        renderOccasionChips();
        await renderRails([]);
        setStatus('Recommendations unavailable right now.', 'error');
      }
    }

    rootNode.addEventListener('click', event => {
      const addPack = event.target.closest('[data-styles-that-pair-add]');
      if (addPack) {
        const card = addPack.closest('[data-hla-product-card]');
        if (!card) return;
        const helpers = helperApi();
        const items = helpers.cardAddItems ? helpers.cardAddItems([card]) : cartItemsFromItems([itemFromCard(card)]);
        if (items.length) {
          event.preventDefault();
          addItemsToCart(items, addPack);
        }
        return;
      }

      const chip = event.target.closest('[data-hla-look-occasion]');
      if (chip) {
        event.preventDefault();
        selectOccasion(chip.dataset.hlaLookOccasion);
        return;
      }

      const clear = event.target.closest('[data-hla-look-clear]');
      if (clear) {
        event.preventDefault();
        clearSlot(clear.dataset.hlaLookClear);
        return;
      }

      const swap = event.target.closest('[data-hla-look-swap]');
      if (swap) {
        event.preventDefault();
        swapSlot(swap.dataset.hlaLookSwap);
        return;
      }

      const quick = event.target.closest('[data-hla-look-quick]');
      if (quick) {
        event.preventDefault();
        const slot = quick.dataset.hlaLookQuick;
        const item = slot === 'seed' ? seedItem : selected[normalizeSlot(slot)];
        favoriteApi()?.openQuickViewItem?.(item, quick);
        return;
      }

      const savePieceButton = event.target.closest('[data-hla-look-save-piece]');
      if (savePieceButton) {
        event.preventDefault();
        savePiece(savePieceButton.dataset.hlaLookSavePiece);
        return;
      }

      if (event.target.closest('[data-hla-look-save]')) {
        event.preventDefault();
        saveLook();
        return;
      }

      if (event.target.closest('[data-hla-look-add]')) {
        event.preventDefault();
        addLook(event.target.closest('[data-hla-look-add]'));
        return;
      }

      const railCard = event.target.closest('[data-hla-look-rail-card]');
      if (railCard && !isInteractiveTarget(event.target)) {
        event.preventDefault();
        placeCard(railCard);
      }
    });

    if (seedForm) {
      seedForm.addEventListener('submit', event => {
        event.preventDefault();
        loadSeed(seedInput ? seedInput.value : '');
      });
    }

    const controller = {
      addLook,
      buildPairApiUrl,
      cartItemsFromItems,
      clearSlot,
      loadSeed,
      normalizeOccasionTag,
      normalizePairResponse,
      normalizeSlot,
      placeCard,
      ready: Promise.resolve(),
      renderRails,
      saveLook,
      selectOccasion,
      state: () => ({
        activeOccasion,
        allPairs: allPairs.slice(),
        seedHandle,
        seedItem,
        selected: { ...selected },
      }),
      swapSlot,
    };
    rootNode._hlaBuildLookController = controller;

    const initialSeed = seedFromLocation() || normalizeHandle(rootNode.dataset.defaultSeed) || DEFAULT_SEED_HANDLE;
    controller.ready = loadSeed(initialSeed);
    return controller;
  }

  function initAll(doc = root.document) {
    if (!doc) return [];
    return Array.from(doc.querySelectorAll('[data-hla-build-look]')).map(initBuilder);
  }

  return {
    buildPairApiUrl,
    cartItemsFromItems,
    initAll,
    initBuilder,
    deriveLineFromProduct,
    normalizeOccasionTag,
    normalizePairResponse,
    normalizeSlot,
  };
});
